import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../../api/client';

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional', desc: 'Formal, concise, business-oriented' },
  { value: 'friendly', label: 'Friendly', desc: 'Warm, conversational, approachable' },
  { value: 'consultative', label: 'Consultative', desc: 'Helpful, advisory, detail-oriented' },
  { value: 'neutral', label: 'Neutral', desc: 'Balanced, clear, no strong personality' },
];

export default function AISettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    tone: 'neutral',
    bio: '',
    instructions: '',
  });
  const [prefs, setPrefs] = useState({
    texting_enabled: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ctxRes, prefRes] = await Promise.all([
        api.get('/accounts/ai-context/'),
        api.get('/accounts/preferences/'),
      ]);
      const ctx = ctxRes.data || {};
      setForm({
        tone: ctx.tone || 'neutral',
        bio: ctx.bio || '',
        instructions: ctx.instructions || '',
      });
      const p = (prefRes.data?.preferences || prefRes.data || {}).ai_settings || {};
      setPrefs({
        // Read the key the backend gate actually reads (ai_settings.texting_enabled).
        // Fall back to the legacy ai_texting_enabled for display continuity so a
        // tenant who toggled the old (no-op) key still sees their last choice.
        texting_enabled: (p.texting_enabled ?? p.ai_texting_enabled) || false,
      });
    } catch {
      toast.error('Failed to load AI settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveContext = async () => {
    setSaving(true);
    try {
      await api.patch('/accounts/ai-context/', form);
      toast.success('AI context saved');
    } catch {
      toast.error('Failed to save AI context');
    } finally {
      setSaving(false);
    }
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      await api.patch('/accounts/preferences/', {
        preferences: { ai_settings: prefs },
      });
      toast.success('AI preferences saved');
    } catch {
      toast.error('Failed to save AI preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="tenant-settings-tab-panel"><p>Loading AI settings...</p></div>;

  return (
    <div className="tenant-settings-tab-panel">
      {/* AI Context — genuinely wired to /accounts/ai-context/ */}
      <section className="tenant-settings-panel">
        <h3>AI Persona</h3>
        <p className="tenant-settings-sub">
          This context is sent to the AI assistant with every interaction — replies, drafts, summaries, and voice responses all use it.
        </p>

        <div style={{ marginTop: 14 }}>
          <label className="tenant-settings-field">
            <span>Tone</span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {TONE_OPTIONS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  className={`tenant-btn ${form.tone === t.value ? 'tenant-btn-primary' : 'tenant-btn-secondary'}`}
                  onClick={() => setForm((p) => ({ ...p, tone: t.value }))}
                  style={{ fontSize: '0.82rem' }}
                  title={t.desc}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </label>
        </div>

        <label className="tenant-settings-field" style={{ marginTop: 14 }}>
          <span>Company Bio</span>
          <textarea
            value={form.bio}
            onChange={(e) => setForm((p) => ({ ...p, bio: e.target.value }))}
            rows={4}
            placeholder="Describe your company, services, and what makes you unique. The AI uses this to sound like part of your team."
          />
          <small>Example: "We're a family-owned ranch venue in Arcadia, FL specializing in weddings, birthday parties, and photo sessions. We pride ourselves on a personal, rustic experience."</small>
        </label>

        <label className="tenant-settings-field" style={{ marginTop: 14 }}>
          <span>Special Instructions</span>
          <textarea
            value={form.instructions}
            onChange={(e) => setForm((p) => ({ ...p, instructions: e.target.value }))}
            rows={3}
            placeholder="Rules the AI must follow — e.g. 'Never offer discounts without manager approval', 'Always mention our Saturday availability'."
          />
        </label>

        <button className="tenant-btn tenant-btn-primary" onClick={saveContext} disabled={saving} style={{ marginTop: 12 }}>
          {saving ? 'Saving...' : 'Save AI Context'}
        </button>
      </section>

      {/* AI Automation — wired to preferences.ai_settings */}
      <section className="tenant-settings-panel">
        <h3>AI Automation</h3>
        <p className="tenant-settings-sub">Controls for AI auto-texting and availability.</p>

        <div className="tenant-toggle-row" style={{ marginTop: 14 }}>
          <div>
            <strong>AI Texting</strong>
            <p style={{ fontSize: '0.82rem', color: 'var(--tenant-text-muted)' }}>
              Allow the AI to send SMS replies to leads automatically.
            </p>
          </div>
          <label className="tenant-switch">
            <input type="checkbox" checked={prefs.texting_enabled} onChange={(e) => setPrefs((p) => ({ ...p, texting_enabled: e.target.checked }))} />
            <span className="tenant-switch-slider" />
          </label>
        </div>

        {/* Active-hours / after-hours removed: these wrote preferences.ai_settings
            keys nothing reads. Quiet hours are one representation now —
            Communications → quiet hours (TenantCommsSettings, enforced at
            comm_app _within_quiet_hours). See docs/SETTINGS_NOOP_AUDIT.md #2. */}

        <button className="tenant-btn tenant-btn-primary" onClick={savePrefs} disabled={saving} style={{ marginTop: 12 }}>
          {saving ? 'Saving...' : 'Save Automation Settings'}
        </button>
      </section>

      {/* Deferred notice */}
      <section className="tenant-settings-panel" style={{ opacity: 0.6 }}>
        <h3>Coming Soon</h3>
        <p className="tenant-settings-sub">
          First-touch template selection, auto-follow-up delay, and staff availability grid are being wired to production endpoints.
        </p>
      </section>
    </div>
  );
}
